<template>
  <div id="stock-list">
    <div class="container-fluid pt-4 px-4">
      <v-card flat rounded="lg" class="stock-list-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <!-- Left section -->
          <div class="d-flex align-center flex-wrap ga-4">
            <span class="text-h6 font-weight-medium">Stock List</span>

            <div class="d-flex align-center ga-3">
              <v-checkbox
                v-model="advanced_filters"
                :value="'only_free_stocks'"
                density="compact"
                hide-details
                label="Only Free Stocks"
              />
              <v-checkbox
                v-model="advanced_filters"
                :value="'only_portfolio_stocks'"
                density="compact"
                hide-details
                label="Only Portfolio Stocks"
              />
            </div>

            <div class="d-flex align-center ga-2">
              <v-btn
                color="primary"
                variant="tonal"
                rounded="lg"
                size="small"
                @click="applyFilters"
              >
                Search
              </v-btn>

              <v-btn
                variant="text"
                rounded="lg"
                size="small"
                @click="resetFilters"
              >
                Reset
              </v-btn>
            </div>
          </div>

          <!-- Right section -->
          <div class="d-flex align-center ga-1">
            <v-btn icon variant="text" size="small" rounded="lg" @click="toggleExpand" title="Column Select">
              <font-awesome-icon :icon="['fas', 'table-columns']" />
            </v-btn>
            <v-btn icon variant="text" size="small" rounded="lg" @click="toggleFilterExpand" title="Filter">
              <font-awesome-icon :icon="['fas', 'filter']" />
            </v-btn>
            <v-btn icon variant="text" size="small" rounded="lg" color="primary" @click="exportToExcel" title="Export CSV">
              <v-icon icon="mdi-download" />
            </v-btn>
          </div>
        </v-card-title>

        <div v-if="isExpanded" class="panel-section" id="stock-list-headers">
          <v-row>
            <v-col
              v-for="(header, index) in grid.headers"
              :key="index"
              cols="12"
              sm="6"
              md="3"
            >
              <v-checkbox
                v-model="selected_columns"
                :value="header.key"
                flat
                class="d-flex"
                :label="header.title"
                :placeholder="header.title"
              />
            </v-col>
          </v-row>
        </div>

        <div v-if="isFilterExpanded" class="panel-section" id="stock-list-header-filters">
          <v-row dense>
            <v-col
              v-for="(header, index) in grid.headers"
              :key="index"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <component
                :is="getFilterComponent(header.filterType)"
                v-if="getFilterComponent(header.filterType)"
                v-model="grid.filters[header.key]"
                :options="filterOptions[header.key] || []"
                :label="header.title"
                :sort="header.sort"
                :order="header.order"
              />
            </v-col>
          </v-row>
        </div>

        <v-data-table
          v-model:expanded="expanded"
          :headers="filteredHeaders"
          :items="stockListPlatformWise.stockData"
          item-value="name"
          show-expand
          fixed-header
          :items-per-page-options="[10, 15, 20, 30, 50, 100, 200]"
          :items-per-page="5"
          class="custom-data-table"
        >
          <template #thead>
            <thead>
              <tr class="summary-row">
                <td v-for="(header, i) in filteredHeaders" :key="i">
                  {{ getSummaryValue(header.key) }}
                </td>
              </tr>
            </thead>
          </template>

          <template v-slot:[`item.code`]="{ item }">
            <router-link
              target="_blank"
              name="stock-view"
              :to="{ path: '/stock/view', query: { id: item.code } }"
            >
              {{ item.code }}
            </router-link>
          </template>

          <template v-slot:[`item.market_price`]="{ item }">
            <span>{{ $helper.formatPrice(item.market_price) }}</span>
          </template>
          <template v-slot:[`item.avg_price`]="{ item }">
            <span>{{ $helper.formatPrice(item.avg_price) }}</span>
          </template>
          <template v-slot:[`item.investment`]="{ item }">
            <span>{{ $helper.formatPrice(item.investment) }}</span>
          </template>
          <template v-slot:[`item.current_value`]="{ item }">
            <span>{{ $helper.formatPrice(item.current_value) }}</span>
          </template>
          <template v-slot:[`item.profit_loss`]="{ item }">
            <span :class="item.profit_loss >= 0 ? 'text-success' : 'text-error'">{{ $helper.formatPrice(item.profit_loss) }}</span>
          </template>
          <template v-slot:[`item.profit_loss_per`]="{ item }">
            <span :class="item.profit_loss_per >= 0 ? 'text-success' : 'text-error'">{{ $helper.formatPercentage(item.profit_loss_per) }}</span>
          </template>
          <template v-slot:[`item.perches_date`]="{ item }">
            <span>{{ $helper.formatDate(item.perches_date, true) }}</span>
          </template>
          <template v-slot:[`item.updated_at`]="{ item }">
            <span>{{ $helper.formatDate(item.updated_at, true) }}</span>
          </template>
          <template v-slot:[`item.percentage_to_sell`]="{ item }">
            <span>{{ $helper.formatPercentage(item.percentage_to_sell / 100) }}</span>
          </template>
          <template v-slot:[`item.indices`]="{ item }">
            <div class="d-flex flex-wrap ga-1">
              <v-tooltip
                v-for="idx in item.indices"
                :key="idx.code"
                location="top"
              >
                <template v-slot:activator="{ props }">
                  <v-chip
                    v-bind="props"
                    size="x-small"
                    :variant="idx.membership_type === 'constituent' ? 'flat' : 'outlined'"
                    :color="idx.membership_type === 'constituent' ? 'primary' : 'default'"
                  >
                    {{ idx.name }}
                  </v-chip>
                </template>
                <span>{{ idx.comments || (idx.membership_type === 'constituent' ? 'Verified index constituent' : 'Sector-linked, not a verified constituent') }}</span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:expanded-row="{ columns, item }">
            <tr class="sub-row-wrap">
              <td :colspan="columns.length">
                <table class="sub-table">
                  <thead>
                    <tr>
                      <th v-for="(field, key) in filteredSubHeaders" :key="key">{{ field.title }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="rows in item.stock" :key="rows.stock_id">
                      <td
                        v-for="(field, key) in filteredSubHeaders"
                        :key="key"
                        class="sub-cell"
                        :class="{
                          'text-success': ['profit_loss', 'profit_loss_per'].includes(field.key) && rows[field.key] >= 0,
                          'text-error': ['profit_loss', 'profit_loss_per'].includes(field.key) && rows[field.key] < 0,
                        }"
                      >
                        <template v-if="field.key === 'name'">
                          <span class="platform-dot" :style="{ 'background-color': getPlatformColor(rows.platform_id) }"></span>
                          {{ getPlatformName(rows.platform_id) }}
                        </template>

                        <template v-else-if="['investment', 'avg_price', 'market_price', 'profit_loss', 'current_value'].includes(field.key)">
                          {{ $helper.formatPrice(rows[field.key]) }}
                        </template>

                        <template v-else-if="field.key === 'profit_loss_per'">
                          {{ $helper.formatPercentage(rows[field.key]) }}
                        </template>

                        <template v-else-if="field.key === 'percentage_to_sell'">
                          {{ $helper.formatPercentage(rows[field.key] / 100) }}
                        </template>

                        <template v-else-if="['perches_date', 'updated_at'].includes(field.key)">
                          {{ $helper.formatDate(rows[field.key], true, true) || '' }}
                        </template>

                        <template v-else>
                          {{ rows[field.key] != null ? String(rows[field.key]).replace(/,/g, '') : '' }}
                        </template>
                      </td>
                    </tr>
                  </tbody>

                </table>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import * as XLSX from 'xlsx';
import { storeToRefs } from 'pinia';
import { useAlertStore, platformStore } from '@/stores';
import {
  //SingleSelectFilter,
  MultiSelectFilter,
  RangeFilter,
  DateRangeFilter,
  CheckboxFilter,
  //RadioFilter,
  TextFilter,
  //NumberFilter,
} from '@/components/Report/filter/index';

import gql from 'graphql-tag';

const STOCK_LIST_PLATFORM_WISE = gql`
  query StockListPlatformWise($platformIds: [ID], $advancedFilters: [String]) {
    stockListPlatformWise(platform_ids: $platformIds, advanced_filters: $advancedFilters) {
      stockData {
        stock_id
        name
        code
        exchange_id
        qty
        investment
        current_value
        avg_price
        market_price
        profit_loss
        profit_loss_per
        platform_id
        sector_id
        perches_date
        track
        fifty_two_week_low
        fifty_two_week_high
        updated_at
        total_sell
        tags
        comments
        percentage_to_sell
        qty_to_sell
        indices {
          code
          name
          membership_type
          comments
        }
        stock {
          stock_id
          name
          code
          exchange_id
          qty
          investment
          current_value
          avg_price
          market_price
          profit_loss
          profit_loss_per
          platform_id
          sector_id
          perches_date
          track
          fifty_two_week_low
          fifty_two_week_high
          updated_at
          tags
          comments
          percentage_to_sell
          qty_to_sell
        }
      }
      gridTotals {
        qty
        investment
        current_value
        avg_price
        market_price
        profit_loss
        profit_loss_per
      }
    }
  }
`;

// RangeFilter/DateRangeFilter emit rich objects ({type,value,value2} /
// {from,to}), not plain arrays -- null lets those components fall back to
// their own empty defaults instead of receiving a shape they don't expect.
function defaultFilters() {
  return {
    stock_id: '',
    name: [],
    code: [],
    exchange_id: [],
    qty: null,
    investment: null,
    avg_price: null,
    total_sell: null,
    profit_loss: null,
    profit_loss_per: null,
    perches_date: null,
    updated_at: null,
    comments: '',
    tags: [],
    percentage_to_sell: null,
    qty_to_sell: null,
  };
}

export default {
  name: 'Stock-list',
  data() {
    return {
      expanded: [],
      grid: {
        headers: [
          { title: 'Stock Id', key: 'stock_id', filterType: 'TextFilter' },
          { title: 'Name', key: 'name', filterType: 'MultiSelectFilter', option: { label: 'name', value: 'stock_id', model: 'Stock' }, sort: 'title', order: 'asc' },
          { title: 'Code', key: 'code', filterType: 'MultiSelectFilter', option: { label: 'code', value: 'stock_id', model: 'Stock' }, sort: 'title', order: 'asc' },
          { title: 'Exchange', key: 'exchange_id', filterType: 'MultiSelectFilter', option: { label: 'name', value: 'exchange_id', model: 'Exchange' }, sort: 'title', order: 'asc' },
          { title: 'Qty', key: 'qty', filterType: 'RangeFilter' },
          { title: 'Price', key: 'market_price', filterType: 'RangeFilter' },
          { title: '52W Low', key: 'fifty_two_week_low', filterType: 'RangeFilter' },
          { title: '52W High', key: 'fifty_two_week_high', filterType: 'RangeFilter' },
          { title: 'Average Price', key: 'avg_price', filterType: 'RangeFilter' },
          { title: 'Investment', key: 'investment', filterType: 'RangeFilter' },
          { title: 'Current Value', key: 'current_value', filterType: 'RangeFilter' },
          { title: 'Total Sell', key: 'total_sell', filterType: 'RangeFilter' },
          { title: 'Profit/Loss', key: 'profit_loss', filterType: 'RangeFilter' },
          { title: 'Profit/Loss(%)', key: 'profit_loss_per', filterType: 'RangeFilter' },
          { title: 'Perches Date', key: 'perches_date', filterType: 'DateRangeFilter' },
          { title: 'Updated At', key: 'updated_at', filterType: 'DateRangeFilter' },
          { title: 'Comments', key: 'comments', filterType: 'TextFilter' },
          { title: 'Tags', key: 'tags', filterType: 'MultiSelectFilter', option: { label: 'tags', value: 'tags', model: 'Stock' }, sort: 'title', order: 'asc' },
          { title: 'Index', key: 'indices' },
          { title: 'Percentage To Sell', key: 'percentage_to_sell', filterType: 'RangeFilter' },
          { title: 'Qty To Sell', key: 'qty_to_sell', filterType: 'RangeFilter' },
        ],
        sub_headers: [
          { title: 'Stock Id', key: 'stock_id' },
          { title: 'Name', key: 'name' },
          { title: 'Code', key: 'code' },
          { title: 'Platform', key: 'platform_id' },
          { title: 'Exchange', key: 'exchange_id' },
          { title: 'Qty', key: 'qty' },
          { title: 'Price', key: 'market_price' },
          { title: '52 Week Low', key: 'fifty_two_week_low'},
          { title: '52 Week High', key: 'fifty_two_week_high'},
          { title: 'Average Price', key: 'avg_price' },
          { title: 'Investment', key: 'investment' },
          { title: 'Current Value', key: 'current_value' },
          { title: 'Total Sell', key: 'total_sell' },
          { title: 'Profit/Loss', key: 'profit_loss' },
          { title: 'Profit/Loss(%)', key: 'profit_loss_per' },
          { title: 'Perches Date', key: 'perches_date' },
          { title: 'Updated At', key: 'updated_at' },
          { title: 'Comments', key: 'comments' },
          { title: 'Tags', key: 'tags' },
          { title: 'Percentage To Sell', key: 'percentage_to_sell' },
          { title: 'Qty To Sell', key: 'qty_to_sell' },
        ],
        filters: defaultFilters(),
      },
      stockListPlatformWise: [],
      selected_columns: [],
      advanced_filters: ['only_portfolio_stocks'],
      isExpanded: false,
      isFilterExpanded: false,
      filterOptions: {}
    };
  },
  computed: {
    filteredHeaders() {
      return this.grid.headers.filter(header => this.selected_columns.includes(header.key));
    },
    filteredSubHeaders() {
      return this.grid.sub_headers.filter(header => this.selected_columns.includes(header.key));
    },
    combinedAdvancedFilters() {
      const filters = [];

      this.grid.headers.forEach(header => {
        const key = header.key;
        const value = this.grid.filters[key];

        switch (header.filterType) {
          // DateRangeFilter emits { from, to } (see DateRangeFilter.vue).
          case 'DateRangeFilter':
            if (value && value.from && value.to) {
              filters.push(`${key}:between:${value.from}:${value.to}`);
            }
            break;

          // RangeFilter emits { type, value, value2 } where type is one of
          // multiple/equal/greater_than/less_than/between (see RangeFilter.vue) --
          // the backend already understands all of these operators.
          case 'RangeFilter':
            if (value && typeof value === 'object') {
              const { type, value: v1, value2: v2 } = value;
              if (type === 'between' && v1 != null && v1 !== '' && v2 != null && v2 !== '') {
                filters.push(`${key}:between:${v1}:${v2}`);
              } else if (['equal', 'greater_than', 'less_than'].includes(type) && v1 != null && v1 !== '') {
                filters.push(`${key}:${type}:${v1}`);
              } else if (type === 'multiple' && Array.isArray(v1) && v1.length > 0) {
                filters.push(`${key}:in:${v1.join(',')}`);
              }
            }
            break;

          case 'MultiSelectFilter':
          case 'CheckboxFilter':
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              value.some(v => v !== '' && v != null)
            ) {
              // The Name/Code dropdowns select stock_id values under the hood
              // (so users can search by name/code text but match precisely),
              // so route those through the stock_id column instead of a text
              // LIKE match against the name/code columns themselves.
              const filterKey = (key === 'name' || key === 'code') ? 'stock_id' : key;
              filters.push(`${filterKey}:${value.join(',')}`);
            }
            break;

          case 'SingleSelectFilter':
            if (value !== '' && value != null) {
              filters.push(`${key}:${value}`);
            }
            break;

          case 'TextFilter':
            if (typeof value === 'string' && value.trim() !== '') {
              filters.push(`${key}:${value.trim()}`);
            }
            break;

          default:
            break;
        }

      });
      const routeQuery = this.$route?.query || {};
      ['sector_id', 'stock_id', 'name', 'code', 'exchange_id', 'comments', 'tags'].forEach((key) => {
        const value = routeQuery[key];
        const normalizedValue = Array.isArray(value)
          ? value.filter(Boolean).join(',')
          : value;

        if (normalizedValue != null && String(normalizedValue).trim() !== '') {
          filters.push(`${key}:${String(normalizedValue).trim()}`);
        }
      });

      return [...this.advanced_filters, ...filters];
    },
  },
  methods: {
    resetFilters() {
      this.grid.filters = defaultFilters();
      this.advanced_filters = ['only_portfolio_stocks'];
    },
    getSelectedColumnsFromLocalStorage() {
      const storedColumns = JSON.parse(localStorage.getItem('selected_columns'));
      return storedColumns ? storedColumns : this.grid.headers.map(header => header.key);
    },
    updateSelectedColumns() {
      localStorage.setItem('selected_columns', JSON.stringify(this.selected_columns));
    },
    
    exportToExcel() {
        const selectedCols = this.selected_columns;

        const subHeaders = this.grid.sub_headers
          .filter(col => selectedCols.includes(col.key))
          .map(col => col.title);

        const platformHeaders = [
          'Platform Name',
          'Total Stock',
          'Total Investment',
          'Current Value',
          'Total Profit',
          'Profit/Loss %',
        ];

        const subRows: any[] = [];
        const platformRows: any[] = [];

        // Map to hold platform-wise totals
        const platformSummaryMap = new Map<string, {
          investment: number;
          profit: number;
          stockCount: number; // Count of stock entries
          current_value: number;
        }>();

        this.stockListPlatformWise.stockData.forEach(item => {
          item.stock.forEach(stockItem => {
            // Create sub row (Stock Sheet)
            const row: any = {};
            this.grid.sub_headers
              .filter(col => selectedCols.includes(col.key))
              .forEach(col => {
                const key = col.key;
                const value = stockItem[key];

                if (['investment', 'avg_price', 'market_price', 'profit_loss', 'current_value'].includes(key)) {
                  row[col.title] = value != null ? value : '';
                } else if (key === 'profit_loss_per' || key === 'percentage_to_sell') {
                  row[col.title] = value != null ? value : '';
                } else if (['perches_date', 'updated_at'].includes(key)) {
                  row[col.title] = this.$helper.formatDate(value, true, true) || '';
                } else if (key === 'platform_id') {
                  row[col.title] = this.getPlatformName(value);
                } else {
                  row[col.title] = value != null ? String(value).replace(/,/g, '') : '';
                }
              });
            subRows.push(row);

            // Build platform summary
            const platformId = String(stockItem.platform_id);
            const current = platformSummaryMap.get(platformId) || {
              investment: 0,
              profit: 0,
              stockCount: 0, // Now counts how many stock entries
              current_value: 0
            };

            current.investment += stockItem.investment || 0;
            current.profit += stockItem.profit_loss || 0;
            current.stockCount += 1; // Increment by 1 for each stock row
            current.current_value += stockItem.current_value || 0;

            platformSummaryMap.set(platformId, current);
          });
        });

        // Push platform summary rows
        platformSummaryMap.forEach((summary, platformId) => {
          const profitLossPer = summary.investment !== 0
            ? summary.profit / summary.investment
            : 0;

          platformRows.push({
            'Platform Name': this.getPlatformName(platformId),
            'Total Stock': summary.stockCount, // updated from qty
            'Total Investment': this.$helper.formatPrice(summary.investment),
            'Current Value': this.$helper.formatPrice(summary.current_value),
            'Total Profit': this.$helper.formatPrice(summary.profit),
            'Profit/Loss %': this.$helper.formatPercentage(profitLossPer),
          });
        });

        // Create Excel sheets
        const stockSheet = XLSX.utils.json_to_sheet(subRows, { header: subHeaders });
        const platformSheet = XLSX.utils.json_to_sheet(platformRows, { header: platformHeaders });

        // Add to workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, stockSheet, 'Stock');
        XLSX.utils.book_append_sheet(workbook, platformSheet, 'Platform');

        // Save the file
        XLSX.writeFile(workbook, 'StockList.xlsx');
    },

    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    toggleFilterExpand() {
      this.isFilterExpanded = !this.isFilterExpanded;
    },
    getSummaryValue(key) {
      // Check if gridTotals is defined and contains the key
      if (this.stockListPlatformWise.gridTotals && this.stockListPlatformWise.gridTotals[key] !== undefined) {
        // Define columns for specific formatting
        const priceColumns = ['investment', 'avg_price', 'profit_loss', 'current_value'];
        const perColumns = ['profit_loss_per'];

        // Format value based on key
        if (priceColumns.includes(key)) {
          // Format as price
          return this.$helper.formatPrice(this.stockListPlatformWise.gridTotals[key]);
        } else if (perColumns.includes(key)) {
          // Format as percentage
          return this.$helper.formatPercentage(this.stockListPlatformWise.gridTotals[key]);
        } else {
          // Return raw value for other keys
          return this.stockListPlatformWise.gridTotals[key];
        }
      }
      // Return an empty string if key is not found
      return "";
    },
    getFilterComponent(type) {
      const map = {
        TextFilter,
        MultiSelectFilter,
        RangeFilter,
        DateRangeFilter,
        CheckboxFilter,
      };
      return map[type] || null;
    },
    getPlatformName(id) {
      const platform = this.platformList.find(p => p.platform_id == id);
      return platform ? platform.name : '';
    },
    getPlatformColor(id) {
      const platform = this.platformList.find(p => p.platform_id == id);
      return platform ? platform.color : '#ffffff';
    },
    async fetchOptions() {
      const headers = this.grid.headers;

      // Fetch options for each header
      for (const { key, option } of headers) {
        if (option?.label && option?.value && option?.model) {
            this.filterOptions[key] = await this.$optionHelper.getOptionsFromGraphQL(
                this.$apollo,
                option.label,
                option.value,
                option.model
            );
        }
      }
    },

    applyFilters() {
      this.fetchStockList();
    },
    fetchStockList() {
    this.$apollo.query({
      query: STOCK_LIST_PLATFORM_WISE,
      variables: {
        platformIds: JSON.parse(this.platformOptions),
        advancedFilters: this.combinedAdvancedFilters // ✅ no parentheses
      }
    }).then(({ data }) => {
      this.stockListPlatformWise = data.stockListPlatformWise;
    }).catch(error => {
      console.error('Error fetching stock list:', error);
    });
  }
  },
  watch: {
    selected_columns: {
      handler() {
        this.updateSelectedColumns();
      },
      deep: true,
    },
  },
  mounted() {
    this.selected_columns = this.getSelectedColumnsFromLocalStorage();
  },

  async created() {
    await this.fetchOptions();
  },

  setup() {
    const alertStore = platformStore();
    const { platform, platformList } = storeToRefs(alertStore);
    return {
      alertStore$: useAlertStore(),
      platformList: platformList,
      platformOptions: platform,
    };
  },
  apollo: {
    stockListPlatformWise: {
      query: STOCK_LIST_PLATFORM_WISE,
      variables() {
        return {
          platformIds: JSON.parse(this.platformOptions),
          advancedFilters: this.combinedAdvancedFilters
        };
      },
      error(error) {
        this.alertStore$.error(JSON.stringify(error.message));
      }
    }
  },
};
</script>

<style scoped>
.stock-list-card {
  border: 1px solid #e5e7eb;
}

.panel-section {
  background-color: #fafafa;
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  padding: 16px 20px;
}

/* Modern data-table look: quieter header, subtle row dividers, gentle hover */
.custom-data-table :deep(thead th) {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background-color: #fafafa;
  border-bottom: 1px solid #e5e7eb !important;
}

.custom-data-table :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.custom-data-table :deep(tbody tr:hover) {
  background-color: #f8f9fb;
}

.custom-data-table :deep(tbody td) {
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f2f4 !important;
}

.custom-data-table .summary-row td {
  background-color: #f5f6f8;
  font-weight: 600;
  font-size: 0.875rem;
  border-bottom: 2px solid #e5e7eb;
  padding: 10px 16px;
}

/* Nested platform-breakdown table on row expand */
.sub-row-wrap td {
  padding: 12px 16px !important;
  background-color: #fcfcfd;
}

.sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.sub-table thead th {
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.sub-table .sub-cell {
  padding: 8px 12px;
  border-bottom: 1px solid #f1f2f4;
}

.sub-table tbody tr:last-child .sub-cell {
  border-bottom: none;
}

.platform-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}
</style>
